import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import * as XLSX from 'xlsx'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
} from '@structural-climbing/ui'
import { PageHeader } from '../components/PageHeader'

type StatusFilter = 'all' | 'active' | 'expired' | 'none'
type ExportFormat = 'csv' | 'excel'
type Cell = string | number

/** One row of `GET /members/export`. */
export interface ExportRow {
  kind: 'member' | 'child'
  first_name: string
  last_name: string
  nickname: string
  email: string
  parent_name: string
  phone: string
  gender: string
  date_of_birth: string
  language: string
  email_reminders: boolean | null
  is_public: boolean | null
  member_since: string
  last_visit: string
  total_visits: number
  status: string
  plan_name: string
  starts_at: string
  expires_at: string
  entries_remaining: number | null
  price_paid: number | null
  club_member: boolean
  added_at_desk: boolean
}

type GroupKey = 'identity' | 'preferences' | 'contact' | 'activity' | 'plan'

interface FieldDef {
  key: string
  labelKey: string
  group: GroupKey
  /** The fields the export always had start ticked, so nobody's file changes shape. */
  defaultOn: boolean
  value: (row: ExportRow, t: TFunction) => Cell
}

const yesNo = (v: boolean | null, t: TFunction): Cell =>
  v === null ? '' : v ? t('exports.yes') : t('exports.no')

const FIELD_DEFS: FieldDef[] = [
  {
    key: 'full_name',
    labelKey: 'exports.fieldFullName',
    group: 'identity',
    defaultOn: true,
    value: (r) => [r.first_name, r.last_name].filter(Boolean).join(' '),
  },
  {
    key: 'first_name',
    labelKey: 'exports.fieldFirstName',
    group: 'identity',
    defaultOn: true,
    value: (r) => r.first_name,
  },
  {
    key: 'last_name',
    labelKey: 'exports.fieldLastName',
    group: 'identity',
    defaultOn: true,
    value: (r) => r.last_name,
  },
  {
    key: 'email',
    labelKey: 'exports.fieldEmail',
    group: 'identity',
    defaultOn: true,
    value: (r) => r.email,
  },
  {
    key: 'nickname',
    labelKey: 'exports.fieldNickname',
    group: 'identity',
    defaultOn: true,
    value: (r) => r.nickname,
  },
  {
    key: 'parent_name',
    labelKey: 'exports.fieldParent',
    group: 'identity',
    defaultOn: false,
    value: (r) => r.parent_name,
  },
  {
    key: 'email_reminders',
    labelKey: 'exports.fieldEmailReminders',
    group: 'preferences',
    defaultOn: false,
    value: (r, t) => yesNo(r.email_reminders, t),
  },
  {
    key: 'is_public',
    labelKey: 'exports.fieldPublic',
    group: 'preferences',
    defaultOn: false,
    value: (r, t) => yesNo(r.is_public, t),
  },
  {
    key: 'club_member',
    labelKey: 'exports.fieldClubMember',
    group: 'preferences',
    defaultOn: false,
    value: (r, t) => yesNo(r.kind === 'child' ? null : r.club_member, t),
  },
  {
    key: 'added_at_desk',
    labelKey: 'exports.fieldAddedAtDesk',
    group: 'preferences',
    defaultOn: false,
    value: (r, t) => yesNo(r.kind === 'child' ? null : r.added_at_desk, t),
  },
  {
    key: 'phone',
    labelKey: 'exports.fieldPhone',
    group: 'contact',
    defaultOn: false,
    value: (r) => r.phone,
  },
  {
    key: 'gender',
    labelKey: 'exports.fieldGender',
    group: 'contact',
    defaultOn: false,
    value: (r, t) =>
      r.gender === 'male'
        ? t('exports.genderMale')
        : r.gender === 'female'
          ? t('exports.genderFemale')
          : r.gender,
  },
  {
    key: 'date_of_birth',
    labelKey: 'exports.fieldDateOfBirth',
    group: 'contact',
    defaultOn: false,
    value: (r) => r.date_of_birth,
  },
  {
    key: 'language',
    labelKey: 'exports.fieldLanguage',
    group: 'contact',
    defaultOn: false,
    value: (r) => r.language,
  },
  {
    key: 'member_since',
    labelKey: 'exports.fieldMemberSince',
    group: 'activity',
    defaultOn: false,
    value: (r) => r.member_since,
  },
  {
    key: 'last_visit',
    labelKey: 'exports.fieldLastVisit',
    group: 'activity',
    defaultOn: false,
    value: (r) => r.last_visit,
  },
  {
    key: 'total_visits',
    labelKey: 'exports.fieldTotalVisits',
    group: 'activity',
    defaultOn: false,
    value: (r) => r.total_visits,
  },
  {
    key: 'status',
    labelKey: 'exports.fieldStatus',
    group: 'plan',
    defaultOn: true,
    value: (r, t) =>
      r.status === 'none' ? t('exports.statusNone') : t(`members.subStatus_${r.status}`),
  },
  {
    key: 'plan_name',
    labelKey: 'exports.fieldPlanName',
    group: 'plan',
    defaultOn: true,
    value: (r) => r.plan_name,
  },
  {
    key: 'starts_at',
    labelKey: 'exports.fieldStartsAt',
    group: 'plan',
    defaultOn: false,
    value: (r) => r.starts_at,
  },
  {
    key: 'expires_at',
    labelKey: 'exports.fieldExpiresAt',
    group: 'plan',
    defaultOn: true,
    value: (r) => r.expires_at,
  },
  {
    key: 'entries_remaining',
    labelKey: 'exports.fieldEntriesRemaining',
    group: 'plan',
    defaultOn: true,
    value: (r) => r.entries_remaining ?? '',
  },
  {
    key: 'price_paid',
    labelKey: 'exports.fieldPricePaid',
    group: 'plan',
    defaultOn: false,
    value: (r) => r.price_paid ?? '',
  },
]

const GROUPS: { key: GroupKey; labelKey: string }[] = [
  { key: 'identity', labelKey: 'exports.groupIdentity' },
  { key: 'preferences', labelKey: 'exports.groupPreferences' },
  { key: 'contact', labelKey: 'exports.groupContact' },
  { key: 'activity', labelKey: 'exports.groupActivity' },
  { key: 'plan', labelKey: 'exports.groupPlan' },
]

const STATUS_FILTERS: { value: StatusFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'exports.filterAll' },
  { value: 'active', labelKey: 'exports.filterActive' },
  { value: 'expired', labelKey: 'exports.filterInactive' },
  { value: 'none', labelKey: 'exports.filterNone' },
]

export function buildRows(
  rows: ExportRow[],
  fields: Record<string, boolean>,
  t: TFunction,
): Record<string, Cell>[] {
  const chosen = FIELD_DEFS.filter((f) => fields[f.key])
  return rows.map((row) => Object.fromEntries(chosen.map((f) => [t(f.labelKey), f.value(row, t)])))
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function downloadCsv(rows: Record<string, Cell>[], filename: string) {
  const headers = Object.keys(rows[0])
  const escape = (v: Cell) => {
    const s = String(v)
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [
    headers.map(escape).join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(',')),
  ]
  // The BOM makes Excel read the file as UTF-8, so ă, î, ș survive.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

function downloadExcel(rows: Record<string, Cell>[], filename: string) {
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Members')
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer
  const blob = new Blob([buf], { type: 'application/octet-stream' })
  triggerBlobDownload(blob, filename)
}

interface ExportMembersModalProps {
  onClose: () => void
}

function ExportMembersModal({ onClose }: ExportMembersModalProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [remindersOnly, setRemindersOnly] = useState(false)
  const [publicOnly, setPublicOnly] = useState(false)
  const [clubOnly, setClubOnly] = useState(false)
  const [includeChildren, setIncludeChildren] = useState(false)
  const [fields, setFields] = useState<Record<string, boolean>>(
    Object.fromEntries(FIELD_DEFS.map((f) => [f.key, f.defaultOn])),
  )
  const [format, setFormat] = useState<ExportFormat>('excel')
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const noFields = !FIELD_DEFS.some((f) => fields[f.key])

  function setGroup(group: GroupKey, on: boolean) {
    setFields((prev) => ({
      ...prev,
      ...Object.fromEntries(FIELD_DEFS.filter((f) => f.group === group).map((f) => [f.key, on])),
    }))
  }

  async function fetchRows(): Promise<ExportRow[]> {
    const params = new URLSearchParams({ status: statusFilter })
    if (remindersOnly) params.set('email_reminders', 'on')
    if (publicOnly) params.set('public', 'yes')
    if (clubOnly) params.set('club', 'yes')
    if (includeChildren) params.set('include_children', '1')
    const token = await getAccessTokenSilently()
    const res = await fetch(`${apiUrl}/members/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error(t('exports.errorLoad'))
    return ((await res.json()) as { rows: ExportRow[] }).rows
  }

  async function handleExport() {
    setExporting(true)
    setError(null)
    try {
      const rows = buildRows(await fetchRows(), fields, t)
      if (rows.length === 0) {
        setError(t('exports.emptyResult'))
        return
      }
      const ts = new Date().toISOString().slice(0, 10)
      if (format === 'csv') downloadCsv(rows, `members-${ts}.csv`)
      else downloadExcel(rows, `members-${ts}.xlsx`)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('exports.errorLoad'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{t('exports.modalTitle')}</ModalHeader>
        <ModalBody className="gap-6">
          <RadioGroup
            label={t('exports.filterLabel')}
            orientation="horizontal"
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as StatusFilter)}
          >
            {STATUS_FILTERS.map(({ value, labelKey }) => (
              <Radio key={value} value={value}>
                {t(labelKey)}
              </Radio>
            ))}
          </RadioGroup>

          <div className="flex flex-col gap-2">
            <p className="text-sm text-foreground-500">{t('exports.onlyLabel')}</p>
            <div className="flex flex-col">
              <Checkbox isSelected={remindersOnly} onValueChange={setRemindersOnly} size="sm">
                {t('exports.onlyReminders')}
              </Checkbox>
              <p className="ms-7 text-xs text-foreground-400">{t('exports.remindersHint')}</p>
            </div>
            <Checkbox isSelected={publicOnly} onValueChange={setPublicOnly} size="sm">
              {t('exports.onlyPublic')}
            </Checkbox>
            <Checkbox isSelected={clubOnly} onValueChange={setClubOnly} size="sm">
              {t('exports.onlyClub')}
            </Checkbox>
            <Checkbox isSelected={includeChildren} onValueChange={setIncludeChildren} size="sm">
              {t('exports.includeChildren')}
            </Checkbox>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-sm text-foreground-500">{t('exports.fieldsLabel')}</p>
            {GROUPS.map((group) => {
              const defs = FIELD_DEFS.filter((f) => f.group === group.key)
              const allOn = defs.every((f) => fields[f.key])
              return (
                <div key={group.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{t(group.labelKey)}</p>
                    <Button size="sm" variant="light" onPress={() => setGroup(group.key, !allOn)}>
                      {allOn ? t('exports.selectNone') : t('exports.selectAll')}
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {defs.map(({ key, labelKey }) => (
                      <Checkbox
                        key={key}
                        isSelected={fields[key]}
                        onValueChange={(on) => setFields((prev) => ({ ...prev, [key]: on }))}
                        size="sm"
                      >
                        {t(labelKey)}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          <RadioGroup
            label={t('exports.formatLabel')}
            orientation="horizontal"
            value={format}
            onValueChange={(v) => setFormat(v as ExportFormat)}
          >
            <Radio value="excel">{t('exports.formatExcel')}</Radio>
            <Radio value="csv">{t('exports.formatCsv')}</Radio>
          </RadioGroup>

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={exporting}>
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            onPress={handleExport}
            isDisabled={exporting || noFields}
            startContent={exporting ? <Spinner size="sm" color="current" /> : undefined}
          >
            {exporting ? t('exports.exporting') : t('exports.exportAction')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export function ExportsPage() {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <div className="p-4 sm:p-8">
      <PageHeader title={t('exports.title')} subtitle={t('exports.subtitle')} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="border border-divider">
          <CardHeader className="pb-1">
            <h2 className="text-base font-semibold">{t('exports.membersCardTitle')}</h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <p className="text-sm text-foreground/60">{t('exports.membersCardDescription')}</p>
            <Button color="primary" variant="flat" onPress={() => setModalOpen(true)}>
              {t('exports.exportButton')}
            </Button>
          </CardBody>
        </Card>
      </div>

      {modalOpen && <ExportMembersModal onClose={() => setModalOpen(false)} />}
    </div>
  )
}
